import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';

import { EsriAttribute, EsriCategory, searchEsriAttributes } from '../../../../api/ach/esri';
import { useConfig } from '../../../../hooks/useConfig';
import useDebounce from '../../../../hooks/useDebounce';

export const CATEGORY_STYLES: Record<EsriCategory, { label: string; color: string }> = {
  race: { label: 'Race/Ethnicity', color: '#7b1fa2' },
  age: { label: 'Age', color: '#1565c0' },
  gender: { label: 'Gender', color: '#00838f' },
  child: { label: 'Children', color: '#e65100' },
  education: { label: 'Education', color: '#283593' },
  HHI: { label: 'Income', color: '#2e7d32' },
  familysize: { label: 'Family Size', color: '#f9a825' },
  market: { label: 'Market Segment', color: '#c62828' },
  demographic: { label: 'Demographic', color: '#546e7a' },
};

const chipSx = (attribute: EsriAttribute) => {
  const style = attribute.category && CATEGORY_STYLES[attribute.category];
  return style
    ? { bgcolor: style.color, color: '#fff', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' } }
    : undefined;
};

interface Props {
  value: EsriAttribute[];
  onChange: (attributes: EsriAttribute[]) => void;
  disabled?: boolean;
}

function EsriAttributeSearch({ value, onChange, disabled }: Props) {
  const { achApiHost } = useConfig();
  const [inputValue, setInputValue] = useState('');
  const query = useDebounce(inputValue.trim(), 300);

  const { data: options = [], isFetching } = useQuery({
    queryKey: ['esriAttributes', achApiHost, query],
    queryFn: () => searchEsriAttributes(achApiHost, query),
    enabled: query.length >= 2,
    retry: false,
  });

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      options={query.length >= 2 ? options : []}
      value={value}
      inputValue={inputValue}
      onInputChange={(_, next) => setInputValue(next)}
      onChange={(_, next) => onChange(next)}
      getOptionLabel={(option) => option.description || option.attribute_name}
      isOptionEqualToValue={(option, selected) => option.attribute_name === selected.attribute_name}
      filterOptions={(x) => x}
      filterSelectedOptions
      loading={isFetching}
      noOptionsText={inputValue.length < 2 ? 'Type to search...' : 'No attributes found'}
      renderInput={(params) => (
        <TextField
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          size="small"
          label="ESRI attributes"
          placeholder="e.g. Female, Age 25, Hispanic, Bachelor..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching && <CircularProgress size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const style = option.category && CATEGORY_STYLES[option.category];
        return (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <li {...props} key={option.attribute_name}>
            <Box>
              <Typography variant="body2">{option.description}</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {option.attribute_name}
                </Typography>
                {style && (
                  <Chip
                    label={style.label}
                    size="small"
                    sx={{ height: 18, fontSize: '0.65rem', bgcolor: style.color, color: '#fff' }}
                  />
                )}
              </Box>
            </Box>
          </li>
        );
      }}
      renderTags={(selected, getTagProps) =>
        selected.map((option, index) => (
          <Chip
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...getTagProps({ index })}
            key={option.attribute_name}
            label={option.description || option.attribute_name}
            size="small"
            sx={chipSx(option)}
          />
        ))
      }
    />
  );
}

export default EsriAttributeSearch;
